import apiV2 from '../api/v2';
import { toApiError } from './apiErrors';

// Accounts > Practice detail > Details > Files section. Backed by
// /api/v2/practices/:practiceId/files - admin-only document management
// (signed agreements, misc practice paperwork), NOT the practice-facing
// patient upload flow (see services/patientFiles.ts, a different domain
// entirely - hence this file is named practiceDocuments.ts rather than
// practiceFiles.ts, to keep the two unmistakably distinct at a glance).

export type PracticeDocumentType = 'agreement' | 'other';

export interface PracticeDocument {
    id: string;
    fileId: string;
    documentType: PracticeDocumentType;
    description: string | null;
    originalFilename: string | null;
    mimeType: string;
    sizeBytes: number;
    createdAt: string;
    createdByUserId: string;
}

export interface PracticeDocumentDownloadUrl {
    url: string;
    expiresAt: string;
}

export async function fetchPracticeDocuments(practiceId: string): Promise<PracticeDocument[]> {
    try {
        const response = await apiV2.get<{ success: boolean; data: PracticeDocument[] }>(
            `/practices/${practiceId}/files`
        );
        return response.data.data;
    } catch (err) {
        throw toApiError(err);
    }
}

export interface UploadPracticeDocumentInput {
    file: File;
    documentType: PracticeDocumentType;
    description?: string;
}

/**
 * Multipart POST - field name 'file', matching the backend's
 * express-fileupload contract (files.controller.ts's requireDocument).
 * `apiV2` sets a default `Content-Type: application/json` header; the
 * per-request override below is required so axios's transformRequest
 * doesn't JSON-stringify the FormData body instead of sending it as-is (see
 * axios/lib/defaults/index.js's `hasJSONContentType` branch) - the browser
 * then fills in the real multipart boundary itself once the request reaches
 * the XHR/fetch adapter, since FormData bodies always have their Content-Type
 * header cleared there first.
 */
export async function uploadPracticeDocument(
    practiceId: string,
    input: UploadPracticeDocumentInput,
    onProgress?: (percent: number) => void
): Promise<PracticeDocument> {
    try {
        const formData = new FormData();
        formData.append('file', input.file);
        formData.append('documentType', input.documentType);
        if (input.description?.trim()) {
            formData.append('description', input.description.trim());
        }

        const response = await apiV2.post<{ success: boolean; data: PracticeDocument }>(
            `/practices/${practiceId}/files`,
            formData,
            {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (event) => {
                    if (!event.total) return;
                    onProgress?.(Math.round((event.loaded / event.total) * 100));
                }
            }
        );
        return response.data.data;
    } catch (err) {
        throw toApiError(err);
    }
}

export async function fetchPracticeDocumentDownloadUrl(
    practiceId: string,
    practiceFileId: string
): Promise<PracticeDocumentDownloadUrl> {
    try {
        const response = await apiV2.get<{ success: boolean; data: PracticeDocumentDownloadUrl }>(
            `/practices/${practiceId}/files/${practiceFileId}/download-url`
        );
        return response.data.data;
    } catch (err) {
        throw toApiError(err);
    }
}

/** 204 on success - nothing to return. */
export async function deletePracticeDocument(
    practiceId: string,
    practiceFileId: string
): Promise<void> {
    try {
        await apiV2.delete(`/practices/${practiceId}/files/${practiceFileId}`);
    } catch (err) {
        throw toApiError(err);
    }
}
